import React, { useEffect, useState } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { KiteGrid, KiteGridCell, KiteIcon } from '@kite/react-kite';
import {
    MediaLibraryCard,
    MediaLibraryCardProps,
} from '../MediaLibraryCard/MediaLibraryCard';
import { AnalyticsController } from '../../layout/Analytics';
import {
    MediaLibraryVideoPlayer,
    MediaLibraryVideoPlayerProps,
} from '../MediaLibraryVideoPlayer/MediaLibraryVideoPlayer';
import axios from 'axios';
import { HeroImage } from '../HeroImage/HeroImage';
import {
    MediaLibraryTab,
    useMediaLibraryContext,
} from '../../providers/MediaLibraryProvider';
import { useLayoutContext } from '../../providers/LayoutProvider';
import './MediaLibraryPage.scss';
import { isBrowser } from '../../shared/isBrowser';

const imageCategories = [
    `allMediaResourceBroadbandExpansionIma`,
    `allMediaResourceCharterHeadquartersIm`,
    `allMediaResourceSpectrumCallCentersI`,
    `allMediaResourceSpectrumProductsImage`,
    `allMediaResourceSpectrumStoresImage`,
    `allMediaResourceSpectrumTechniciansIm`,
    `allMediaResourceSpectrumVehiclesImage`,
];

export const MEDIA_LIBRARY_TITLE = `Media Library`;
export const MEDIA_LIBRARY_DESC = `All content downloaded from this site may be used for editorial purposes only. Any other use including without limitation, personal or commercial use, is strictly prohibited.`;

export const MediaLibraryPage = ({
    tabName,
    subTabName,
}: {
    tabName: string;
    subTabName?: string;
}) => {
    const [subtab, setSubtab] = useState<MediaLibraryTab>(null);
    const [videoOpen, setVideoOpen] =
        useState<MediaLibraryVideoPlayerProps>(null);
    const { mediaLibraryTabs, setMediaLibraryTabs } = useMediaLibraryContext();
    const { isPolicy } = useLayoutContext();

    enum ResourceType {
        PK_PARENT = `node__press_kit_parent_folder`,
        PK_IMAGE = `media__resource_press_kit_image`,
        PK_DOCUMENT = `media__resource_press_kit_document`,
        PK_VIDEO = `media__resource_press_kit_video`,
        PK_FOLDER = `media__press_kit_folder`,
    }

    const calculateFileSize = (fSize: number) => {
        if (!+fSize) return `0 bytes`;

        const kSize = 1024;
        const decPlace = 2;
        const fileSizes = [`bytes`, `kb`, `mb`, `gb`, `tb`, `pb`];
        const instCalc = Math.floor(Math.log(fSize) / Math.log(kSize));
        return `${parseFloat(
            (fSize / Math.pow(kSize, instCalc)).toFixed(decPlace)
        )} ${fileSizes[instCalc]}`;
    };

    if (isPolicy) {
        if (typeof window !== `undefined`) {
            window.location.replace(`/404`);
        }
        return <></>;
    }
    const tab = mediaLibraryTabs.find((mTab) => mTab.id === tabName);

    const data = useStaticQuery(graphql`
        query {
            nodeLandingPage(path: { alias: { eq: "/media-library" } }) {
                id
                relationships {
                    field_hero_image {
                        field_media_image_1 {
                            alt
                        }
                        relationships {
                            field_media_image_1 {
                                localFile {
                                    childImageSharp {
                                        ...ImageFullWidthSettings
                                    }
                                }
                            }
                        }
                        name
                    }
                }
                field_hero_text {
                    value
                }
                metatag {
                    tag
                    attributes {
                        content
                        href
                        name
                        rel
                    }
                }
                title
            }
            allMediaResourceLogo {
                totalCount
                nodes {
                    __typename
                    field_resource_filesize
                    field_resource_logo_preview_img {
                        alt
                    }
                    name
                    relationships {
                        field_media_file {
                            filename
                            localFile {
                                publicURL
                                extension
                            }
                        }
                        field_resource_logo_preview_img {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceVideo {
                totalCount
                nodes {
                    __typename
                    field_video_length
                    field_media_video_file {
                        description
                    }
                    field_resource_description {
                        value
                    }
                    field_resource_logo_preview_img {
                        alt
                    }
                    name
                    relationships {
                        field_resource_logo_preview_img {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                        field_media_video_file {
                            localFile {
                                publicURL
                            }
                            filesize
                        }
                    }
                }
            }
            allMediaResourceBroadbandExpansionIma {
                totalCount
                nodes {
                    name
                    __typename
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceCharterHeadquartersIm {
                totalCount
                nodes {
                    name
                    __typename
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceSpectrumCallCentersI {
                totalCount
                nodes {
                    name
                    __typename
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceSpectrumProductsImage {
                totalCount
                nodes {
                    __typename
                    name
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceSpectrumVehiclesImage {
                totalCount
                nodes {
                    __typename
                    name
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceSpectrumTechniciansIm {
                totalCount
                nodes {
                    __typename
                    name
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            allMediaResourceSpectrumStoresImage {
                totalCount
                nodes {
                    __typename
                    name
                    field_category_view
                    field_media_in_library
                    field_resource_filesize
                    field_resource_description {
                        value
                    }
                    field_media_image {
                        alt
                    }
                    field_resource_image_height
                    field_resource_image_width
                    relationships {
                        field_media_image {
                            localFile {
                                extension
                                publicURL
                            }
                        }
                        thumbnail {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                    internal {
                        type
                    }
                }
            }
            allNodePressKitParentFolder {
                nodes {
                    created
                    __typename
                    title
                    field_press_kit_description
                    field_image {
                        alt
                    }
                    relationships {
                        field_image {
                            localFile {
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                        }
                        field_press_kits {
                            ... on media__resource_press_kit_image {
                                __typename
                                id
                                name
                                created
                                field_category_view
                                field_media_image {
                                    height
                                    width
                                    alt
                                }
                                relationships {
                                    field_media_image {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                            publicURL
                                            extension
                                        }
                                        filemime
                                        filename
                                        filesize
                                    }
                                    thumbnail {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                    field_resource_logo_preview_img {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                }
                                field_resource_description {
                                    value
                                }
                                field_show_in_media_library
                                field_resource_logo_preview_img {
                                    alt
                                }
                                internal {
                                    type
                                    description
                                }
                            }
                            ... on media__resource_press_kit_document {
                                __typename
                                created
                                id
                                name
                                field_media_document {
                                    value
                                }
                                field_category_view
                                field_resource_filesize
                                field_resource_logo_preview_img {
                                    alt
                                    title
                                }
                                relationships {
                                    field_media_document {
                                        filename
                                        filemime
                                        filesize
                                        localFile {
                                            publicURL
                                            extension
                                        }
                                    }
                                    field_resource_logo_preview_img {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                    thumbnail {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                }
                                internal {
                                    type
                                    description
                                }
                            }
                            ... on media__press_kit_folder {
                                __typename
                                id
                                name
                                created
                                field_resource_description {
                                    value
                                }
                                relationships {
                                    field_media_file {
                                        localFile {
                                            publicURL
                                            extension
                                        }
                                        filesize
                                        filename
                                        filemime
                                    }
                                    field_resource_logo_preview_img {
                                        localFile {
                                            name
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                }
                                field_resource_logo_preview_img {
                                    title
                                    alt
                                }
                                internal {
                                    type
                                }
                            }
                            ... on media__resource_press_kit_video {
                                __typename
                                created
                                id
                                name
                                field_video_length
                                field_media_video_file {
                                    description
                                }
                                field_resource_filesize
                                field_category_view
                                field_resource_logo_preview_img {
                                    alt
                                }
                                relationships {
                                    field_resource_logo_preview_img {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                    field_media_video_file {
                                        localFile {
                                            publicURL
                                            extension
                                        }
                                        filename
                                        filesize
                                        filemime
                                    }
                                    thumbnail {
                                        localFile {
                                            childImageSharp {
                                                gatsbyImageData
                                            }
                                        }
                                    }
                                }
                                internal {
                                    type
                                    description
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const pressKitFolders = [...data.allNodePressKitParentFolder.nodes];

    const logosTab = mediaLibraryTabs.find((tab) => tab.id === `logos`);
    const videosTab = mediaLibraryTabs.find((tab) => tab.id === `videos`);
    const imagesTab = mediaLibraryTabs.find((tab) => tab.id === `images`);
    const pressKitsTab = mediaLibraryTabs.find((tab) => tab.id === `presskits`);

    /**
     * Both the 'images' tab and the 'presskits' tabs include
     * subcategories. This variable is used to determine if we
     * are on the 'images' tab. If not, we assume we are on the
     * 'presskits' tab.
     */
    const isImageTab = tabName === `images`;

    if (!logosTab.count) {
        logosTab.count = data.allMediaResourceLogo?.totalCount;
        logosTab.cards = data.allMediaResourceLogo?.nodes?.map((item) => {
            return {
                title: item.name,
                logo: true,
                details: [
                    item.relationships?.field_media_file?.localFile?.extension +
                        ` file`,
                    calculateFileSize(item.field_resource_filesize),
                ],
                image: item.relationships?.field_resource_logo_preview_img
                    ?.localFile,
                imageAlt: item.field_resource_logo_preview_img?.alt,
                url: {
                    label: `Download`,
                    download: true,
                    href: item.relationships?.field_media_file?.localFile
                        ?.publicURL,
                },
            } as MediaLibraryCardProps;
        });

        setMediaLibraryTabs([...mediaLibraryTabs]);
    }

    if (!videosTab.count) {
        videosTab.count = videosTab.cards =
            data.allMediaResourceVideo?.totalCount;
        videosTab.cards = data.allMediaResourceVideo?.nodes?.map(
            (item) =>
                ({
                    title: item.name,
                    details: [
                        item.field_video_length,
                        calculateFileSize(
                            item.relationships?.field_media_video_file?.filesize
                        ),
                    ],
                    video: true,
                    data: item,
                    image: item.relationships?.field_resource_logo_preview_img
                        ?.localFile,
                    imageAlt: item.field_resource_logo_preview_img?.alt,
                    url: {
                        label: `Download`,
                        download: true,
                        href: item.relationships?.field_media_video_file
                            ?.localFile?.publicURL,
                    },
                } as MediaLibraryCardProps)
        );

        setMediaLibraryTabs([...mediaLibraryTabs]);
    }

    if (!imagesTab.subCategories.length) {
        for (const category of imageCategories) {
            const catData = data[category].nodes;
            const featured = catData.find((item) => item.field_category_view);
            if (!featured) continue;
            const route = featured.name
                ?.toLowerCase()
                .trim()
                .replace(/ /g, `-`);

            imagesTab.subCategories.push({
                name: featured.name,
                description: featured.field_resource_description?.value,
                image: featured.relationships?.thumbnail?.localFile,
                imageAlt: featured.field_media_image?.alt,
                route: route,
                zip: `/static/media/${route}.zip`,
                cards: catData
                    .filter((item) => !item.field_category_view)
                    .map((item) => {
                        return {
                            title: item.name,
                            details: [
                                item.relationships?.field_media_image?.localFile
                                    ?.extension,
                                `${item.field_resource_image_width} x ${item.field_resource_image_height}`,
                                calculateFileSize(item.field_resource_filesize),
                            ],
                            image: item.relationships?.thumbnail?.localFile,
                            imageAlt: item.field_media_image?.alt,
                            url: {
                                label: `Download`,
                                download: true,
                                href: item?.relationships?.field_media_image
                                    ?.localFile?.publicURL,
                            },
                        };
                    }),
            });
        }
        imagesTab.count = imagesTab.subCategories.reduce(
            (prev, current) => prev + current.cards.length,
            0
        );

        setMediaLibraryTabs([...mediaLibraryTabs]);
    }

    if (!pressKitsTab?.count) {
        // Map over the press kit folders to create each subcategory and its cards
        pressKitFolders?.map((pkit) => {
            // Generate the route for the subcategory based on the Parent Press Kit Folder's Title
            const route = pkit.title?.toLowerCase().trim().replace(/ /g, `-`);

            pressKitsTab.subCategories.push({
                name: pkit.title,
                description: pkit?.field_press_kit_description
                    ? `<p>${pkit.field_press_kit_description}</p>`
                    : ``,
                image: pkit.relationships?.field_image?.localFile,
                imageAlt: pkit.field_image?.alt,
                route: route,
                zip: `/static/media/${route}.zip`,
                cards: pkit?.relationships?.field_press_kits
                    ? [...pkit.relationships.field_press_kits]
                          .map((item) => {
                              // set base card props
                              const cardTpl = {
                                  title: item.name,
                                  details: [],
                                  cardThumbnail: undefined,
                                  cardThumbAlt: ``,
                                  image: undefined,
                                  imageAlt: ``,
                                  url: {
                                      label: `Download`,
                                      download: true,
                                      href: null,
                                  },
                                  video: false,
                                  data: null,
                              };

                              // Populate individual card props based on the type of media
                              switch (item.internal.type) {
                                  case ResourceType.PK_DOCUMENT:
                                      cardTpl.details = [
                                          `${item.relationships?.field_media_document?.localFile?.extension} file`,
                                          calculateFileSize(
                                              item.relationships
                                                  ?.field_media_document
                                                  ?.filesize
                                          ),
                                      ];
                                      cardTpl.image =
                                          item.relationships?.field_resource_logo_preview_img?.localFile;
                                      cardTpl.imageAlt =
                                          item.field_resource_logo_preview_img?.alt;
                                      cardTpl.url.href =
                                          item.relationships?.field_media_document?.localFile?.publicURL;
                                      break;

                                  case ResourceType.PK_VIDEO:
                                      cardTpl.details = [
                                          `${item.field_video_length}`,
                                          calculateFileSize(
                                              item.relationships
                                                  ?.field_media_video_file
                                                  ?.filesize
                                          ),
                                      ];
                                      cardTpl.video = true;
                                      cardTpl.data = item;
                                      cardTpl.image =
                                          item.relationships?.field_resource_logo_preview_img?.localFile;
                                      cardTpl.imageAlt =
                                          item.field_resource_logo_preview_img?.alt;
                                      cardTpl.url.href =
                                          item.relationships?.field_media_video_file?.localFile?.publicURL;
                                      break;

                                  case ResourceType.PK_IMAGE:
                                      cardTpl.details = [
                                          `${item.relationships?.field_media_image?.localFile?.extension} file`,
                                          `${item?.relationships?.field_media_image?.localFile?.childImageSharp?.gatsbyImageData?.width} x ${item?.relationships?.field_media_image?.localFile?.childImageSharp?.gatsbyImageData?.height}`,
                                          calculateFileSize(
                                              item.relationships
                                                  ?.field_media_image?.filesize
                                          ),
                                      ];
                                      cardTpl.image =
                                          item.relationships?.thumbnail?.localFile;
                                      cardTpl.imageAlt =
                                          item.field_media_image?.alt;
                                      cardTpl.url.href =
                                          item.relationships?.field_media_image?.localFile?.publicURL;
                                      cardTpl.cardThumbAlt =
                                          item.relationships?.field_resource_logo_preview_img?.alt;
                                      cardTpl.cardThumbnail =
                                          item.relationships?.field_resource_logo_preview_img?.localFile;
                                      break;

                                  case ResourceType.PK_FOLDER:
                                      cardTpl.details = [
                                          `${item.relationships?.field_media_file?.localFile?.extension} file`,
                                          calculateFileSize(
                                              item.relationships
                                                  ?.field_media_file?.filesize
                                          ),
                                      ];
                                      cardTpl.image =
                                          item.relationships?.field_resource_logo_preview_img?.localFile;
                                      cardTpl.imageAlt =
                                          item.field_resource_logo_preview_img?.alt;
                                      cardTpl.url.href =
                                          item.relationships?.field_media_file?.localFile?.publicURL;
                                      cardTpl.cardThumbAlt =
                                          item.relationships?.field_resource_logo_preview_img?.alt;
                                      cardTpl.cardThumbnail =
                                          item.relationships?.thumbnail?.localFile;
                                      break;

                                  default:
                                      break;
                              }

                              return cardTpl as MediaLibraryCardProps;
                          })
                          .sort(
                              (
                                  a: MediaLibraryCardProps,
                                  b: MediaLibraryCardProps
                              ) => {
                                  return new Date(a.created) <
                                      new Date(b.created)
                                      ? 1
                                      : -1;
                              }
                          )
                    : [],
            });
        });

        pressKitsTab.count = pressKitFolders.length;

        setMediaLibraryTabs([...mediaLibraryTabs]);
    }

    useEffect(() => {
        if (isBrowser) {
            window.scrollTo({ top: 0 });
        }
        if (!subtab && subTabName) {
            setSubtab(
                tab.subCategories.find((subcat) => subcat.route === subTabName)
            );
        }
        if (!logosTab.downloadSize) {
            axios
                .head(logosTab.zip)
                .then((res) => {
                    if (`content-length` in res.headers) {
                        logosTab.downloadSize = calculateFileSize(
                            parseInt(res.headers[`content-length`])
                        );
                        setMediaLibraryTabs([...mediaLibraryTabs]);
                    }
                })
                //eslint-disable-next-line
                .catch((ex) => console.error(ex));

            setMediaLibraryTabs([...mediaLibraryTabs]);
        }

        if (!videosTab.downloadSize) {
            axios
                .head(videosTab.zip)
                .then((res) => {
                    if (`content-length` in res.headers) {
                        videosTab.downloadSize = calculateFileSize(
                            parseInt(res.headers[`content-length`])
                        );
                        setMediaLibraryTabs([...mediaLibraryTabs]);
                    }
                })
                //eslint-disable-next-line
                .catch((ex) => console.error(ex));

            setMediaLibraryTabs([...mediaLibraryTabs]);
        }

        for (const subcat of imagesTab.subCategories) {
            if (!subcat.downloadSize) {
                axios
                    .head(subcat.zip)
                    .then((res) => {
                        if (`content-length` in res.headers) {
                            subcat.downloadSize = calculateFileSize(
                                parseInt(res.headers[`content-length`])
                            );
                            setMediaLibraryTabs([...mediaLibraryTabs]);
                        }
                    })
                    //eslint-disable-next-line
                    .catch((ex) => console.error(ex));
            }
        }
        for (const subcat of pressKitsTab.subCategories) {
            if (!subcat.downloadSize) {
                axios
                    .head(subcat.zip)
                    .then((res) => {
                        if (`content-length` in res.headers) {
                            subcat.downloadSize = calculateFileSize(
                                parseInt(res.headers[`content-length`])
                            );
                            setMediaLibraryTabs([...mediaLibraryTabs]);
                        }
                    })
                    //eslint-disable-next-line
                    .catch((ex) => console.error(ex));
            }
        }
    }, []);

    if (!data || !data.nodeLandingPage) {
        return <></>;
    }

    return (
        <>
            <HeroImage
                inputs={{
                    imageAlt:
                        data.nodeLandingPage?.relationships?.field_hero_image
                            ?.field_media_image_1?.alt,
                    heroText: data.nodeLandingPage?.field_hero_text.value,
                    heroTitle: data.nodeLandingPage?.title,
                    image: data.nodeLandingPage?.relationships?.field_hero_image
                        ?.relationships?.field_media_image_1?.localFile,
                }}
            />
            <article className="media-library kite-container">
                <nav>
                    <ul>
                        {mediaLibraryTabs.map((sTab) => (
                            <li
                                key={sTab.id}
                                className={sTab.id === tab.id ? `active` : ``}
                            >
                                <Link
                                    to={`/media-library/${
                                        sTab.id === `presskits`
                                            ? `media-assets`
                                            : sTab.id
                                    }`}
                                    className="typestack-nav-links"
                                >
                                    {sTab.id === `presskits`
                                        ? `Media Assets`
                                        : sTab.name}
                                    ({sTab.count})
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
                {![`images`, `presskits`].includes(tabName) && (
                    <h2>
                        <a
                            className="download-all"
                            href={tab.zip}
                            aria-label={`Download All ${tab.name}`}
                        >
                            <KiteIcon icon="ki-download" size="1.5rem" />
                            Download All {tab.name}
                            {` `}
                            {<>({tab.downloadSize})</>}
                        </a>
                    </h2>
                )}
                {!subtab && (
                    <KiteGrid>
                        {tab.cards &&
                            tab.cards.length > 0 &&
                            tab.cards.map((card, idx) => {
                                const nCard = {
                                    ...card,
                                    setVideoOpen:
                                        card.video === true
                                            ? setVideoOpen
                                            : undefined,
                                };
                                return (
                                    <KiteGridCell col={3} key={idx}>
                                        <MediaLibraryCard {...nCard} />
                                    </KiteGridCell>
                                );
                            })}

                        {tab.subCategories &&
                            tab.subCategories.length > 0 &&
                            tab.subCategories.map((subcat, idx) => (
                                <KiteGridCell col={3} key={idx}>
                                    <MediaLibraryCard
                                        title={subcat.name}
                                        details={[]}
                                        image={subcat.image}
                                        imageAlt={subcat.imageAlt}
                                        url={{
                                            label: isImageTab
                                                ? `View More Images`
                                                : `View All Assets`,
                                            ariaLabel: subcat.name,
                                            internal: true,
                                            href: subcat.route,
                                        }}
                                        subtitle={subcat.description}
                                    />
                                </KiteGridCell>
                            ))}
                    </KiteGrid>
                )}
                {subtab && (
                    <>
                        {isImageTab ? (
                            <Link
                                className="download-all"
                                to="/media-library/images"
                            >
                                <KiteIcon icon="ki-arrow-left" />
                                All Images
                            </Link>
                        ) : (
                            <Link
                                className="download-all"
                                to="/media-library/media-assets"
                            >
                                <KiteIcon icon="ki-arrow-left" />
                                All Media Assets
                            </Link>
                        )}

                        <h2>
                            <a
                                className="download-all"
                                href={subtab.zip}
                                onClick={() => {
                                    AnalyticsController.track({
                                        event: `media download`,
                                        mediaName: `download: All ${tab.name}${subtab.name}`,
                                    });
                                }}
                            >
                                <KiteIcon icon="ki-download" size="1.5rem" />
                                Download All
                                {isImageTab
                                    ? ` ${subtab.name} Images`
                                    : ` Assets`}
                                {` `}
                                {<>({subtab.downloadSize})</>}
                            </a>
                        </h2>
                        <KiteGrid>
                            {subtab.cards.map((card, idx) => {
                                const nCard = {
                                    ...card,
                                    setVideoOpen:
                                        card.video === true
                                            ? setVideoOpen
                                            : undefined,
                                };
                                return (
                                    <KiteGridCell col={3} key={idx}>
                                        <MediaLibraryCard {...nCard} />
                                    </KiteGridCell>
                                );
                            })}
                        </KiteGrid>
                    </>
                )}
                {videoOpen && (
                    <div
                        className="modal"
                        onClick={(evt) => {
                            if (
                                (evt.target as HTMLElement).className ===
                                `modal`
                            ) {
                                setVideoOpen(null);
                            }
                        }}
                    >
                        <MediaLibraryVideoPlayer
                            videoFile={videoOpen.videoFile}
                            title={videoOpen.title}
                            duration={videoOpen.duration}
                            filesize={videoOpen.filesize}
                            setVideoOpen={setVideoOpen}
                        />
                    </div>
                )}
            </article>
        </>
    );
};
